import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import { api, clearToken, errMsg } from "@/lib/api";

const CATS = ["Tax & Regulatory", "Accounting & Financial Reporting", "Business & Finance", "Advisory Perspectives"];
const STATUS = ["draft", "published", "archived"];
const field = "w-full bg-white border border-border-subtle px-3.5 py-2.5 text-charcoal text-[0.9rem] focus:border-navy outline-none transition-colors";
const lbl = "block text-[0.68rem] font-semibold tracking-wider uppercase text-charcoal/60 mb-1.5";

const TYPES = {
  articles: {
    label: "Articles", endpoint: "articles",
    blank: { title: "", category: CATS[0], date: "", excerpt: "", body: "", cover_image: "", status: "draft" },
    fields: [
      { k: "title", t: "text", label: "Title" },
      { k: "category", t: "select", label: "Category", opts: CATS },
      { k: "date", t: "text", label: "Date (e.g. June 2026)" },
      { k: "excerpt", t: "textarea", label: "Short description / excerpt", rows: 2 },
      { k: "body", t: "textarea", label: "Article body (blank line = new paragraph)", rows: 6 },
      { k: "cover_image", t: "text", label: "Cover image URL (optional)" },
      { k: "status", t: "select", label: "Status", opts: STATUS },
    ],
  },
  linkedin: {
    label: "LinkedIn Posts", endpoint: "linkedin",
    blank: { title: "", category: CATS[0], date: "", description: "", url: "", image: "", status: "draft" },
    fields: [
      { k: "title", t: "text", label: "Title" },
      { k: "category", t: "select", label: "Category", opts: CATS },
      { k: "date", t: "text", label: "Date" },
      { k: "description", t: "textarea", label: "Short description", rows: 2 },
      { k: "url", t: "text", label: "LinkedIn URL" },
      { k: "image", t: "text", label: "Image URL (optional)" },
      { k: "status", t: "select", label: "Status", opts: STATUS },
    ],
  },
  opportunities: {
    label: "Careers Opportunities", endpoint: "opportunities",
    blank: { title: "", qualification: "", experience: "", location: "Kanpur", description: "", application: "", status: "draft" },
    fields: [
      { k: "title", t: "text", label: "Role / title" },
      { k: "qualification", t: "text", label: "Qualification" },
      { k: "experience", t: "text", label: "Experience" },
      { k: "location", t: "text", label: "Location" },
      { k: "description", t: "textarea", label: "Description", rows: 4 },
      { k: "application", t: "textarea", label: "Application instructions (optional)", rows: 2 },
      { k: "status", t: "select", label: "Status", opts: STATUS },
    ],
  },
};

const badge = (s) => {
  const c = s === "published" ? "text-green-800 border-green-700/40 bg-green-50"
    : s === "archived" ? "text-charcoal/60 border-border-subtle bg-warm-grey/40"
    : "text-amber-800 border-amber-600/40 bg-amber-50";
  return `text-[0.6rem] font-semibold tracking-widest uppercase border px-2 py-0.5 ${c}`;
};

const Manager = ({ type }) => {
  const cfg = TYPES[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(cfg.blank);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${cfg.endpoint}`);
      setItems(data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [cfg.endpoint]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setEditing(null); setForm(cfg.blank); }, [type]); // eslint-disable-line

  const openNew = () => { setForm(cfg.blank); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...cfg.blank, ...it }); setEditing(it.id); };
  const close = () => { setEditing(null); setForm(cfg.blank); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || form.title.trim().length < 2) return toast.error("Title is required.");
    setBusy(true);
    try {
      const payload = { ...form };
      if (editing === "new") await api.post(`/admin/${cfg.endpoint}`, payload);
      else await api.put(`/admin/${cfg.endpoint}/${editing}`, payload);
      toast.success("Saved.");
      close(); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const setStatus = async (it, status) => {
    try { await api.put(`/admin/${cfg.endpoint}/${it.id}`, { ...cfg.blank, ...it, status }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm("Remove this item permanently?")) return;
    try { await api.delete(`/admin/${cfg.endpoint}/${it.id}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-navy text-2xl">{cfg.label}</h2>
        <button onClick={openNew} className="btn-solid" style={{ padding: "0.7rem 1.2rem" }} data-testid={`admin-new-${type}`}>
          <Plus size={15} /> New
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-charcoal/50"><Loader2 className="animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-14 text-center text-charcoal/50 border border-dashed border-border-subtle">No items yet. Click “New” to add one.</div>
      ) : (
        <div className="border-t border-border-subtle">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-4 py-4 border-b border-border-subtle" data-testid={`admin-row-${type}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className={badge(it.status)}>{it.status}</span>
                  <span className="text-xs text-charcoal/50">{it.category || it.location} · {it.date || ""}</span>
                </div>
                <div className="font-serif text-navy text-lg mt-1 truncate">{it.title}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {it.status !== "published"
                  ? <button onClick={() => setStatus(it, "published")} className="text-xs font-semibold uppercase tracking-wider text-green-800 border border-green-700/40 px-2.5 py-1.5 hover:bg-green-50">Publish</button>
                  : <button onClick={() => setStatus(it, "draft")} className="text-xs font-semibold uppercase tracking-wider text-amber-800 border border-amber-600/40 px-2.5 py-1.5 hover:bg-amber-50">Unpublish</button>}
                <button onClick={() => openEdit(it)} className="p-2 text-navy hover:text-brass" aria-label="Edit"><Pencil size={16} /></button>
                <button onClick={() => remove(it)} className="p-2 text-charcoal/50 hover:text-red-700" aria-label="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(23,38,61,0.4)" }} onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ivory w-full max-w-2xl p-6 sm:p-8" data-testid={`admin-form-${type}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-navy text-2xl">{editing === "new" ? "New" : "Edit"} — {cfg.label}</h3>
              <button type="button" onClick={close} className="p-1 text-charcoal/60 hover:text-navy"><X size={22} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cfg.fields.map((f) => (
                <div key={f.k} className={f.t === "textarea" ? "sm:col-span-2" : ""}>
                  <label className={lbl}>{f.label}</label>
                  {f.t === "textarea" ? (
                    <textarea className={field} rows={f.rows || 3} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
                  ) : f.t === "select" ? (
                    <select className={field} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}>
                      {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className={field} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-7">
              <button type="submit" className="btn-solid" disabled={busy}>{busy ? <Loader2 size={16} className="animate-spin" /> : "Save"}</button>
              <button type="button" onClick={close} className="btn-line">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Account = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 10) return toast.error("New password must be at least 10 characters.");
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password updated."); setCur(""); setNw(""); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md">
      <h2 className="font-serif text-navy text-2xl mb-6">Account</h2>
      <form onSubmit={submit} className="bg-white border border-border-subtle p-6" data-testid="admin-account-form">
        <label className={lbl}>Current password</label>
        <input className={field} type="password" value={cur} onChange={(e) => setCur(e.target.value)} required />
        <label className={`${lbl} mt-4`}>New password (min 10 chars)</label>
        <input className={field} type="password" value={nw} onChange={(e) => setNw(e.target.value)} required />
        <button type="submit" className="btn-solid mt-6" disabled={busy}>{busy ? <Loader2 size={16} className="animate-spin" /> : "Update password"}</button>
      </form>
    </div>
  );
};

const TABS = [["articles", "Articles"], ["linkedin", "LinkedIn Posts"], ["opportunities", "Opportunities"], ["account", "Account"]];

const AdminDashboard = () => {
  const nav = useNavigate();
  const [ok, setOk] = useState(null);
  const [tab, setTab] = useState("articles");

  useEffect(() => {
    api.get("/auth/me").then(() => setOk(true)).catch(() => { clearToken(); nav("/admin/login"); });
  }, [nav]);

  if (ok === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#F7F4EE" }}><Loader2 className="animate-spin text-navy" /></div>;

  const logout = () => { clearToken(); nav("/admin/login"); };

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EE" }}>
      <header className="border-b border-border-subtle bg-ivory/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-serif text-navy text-lg leading-none">Arun Gupta &amp; Co.</div>
              <div className="overline" style={{ fontSize: "0.5rem" }}>Content Admin</div>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-charcoal/70 hover:text-navy" data-testid="admin-logout"><LogOut size={16} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-border-subtle">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-3 text-sm font-medium tracking-wide -mb-px border-b-2 transition-colors ${tab === k ? "border-brass text-navy" : "border-transparent text-charcoal/55 hover:text-navy"}`}>
              {l}
            </button>
          ))}
        </div>
        {tab === "account" ? <Account /> : <Manager type={tab} key={tab} />}
      </div>
    </div>
  );
};

export default AdminDashboard;
